/** @jsx jsx */
import { useState } from 'react';
import { jsx } from '@emotion/core';
import { useTranslation } from 'react-i18next';

import { ChallengePhaseType, ChallengeResultOrderType } from 'interfaces';
import { ChallengeRanking } from 'components';
import {
  ChallengeResultsDataType,
  ChallengeResultsStateType,
} from 'reducers/challenge.reducer';
import { CHALLENGE, CHALLENGE_PHASE } from 'constants/challenges';
import PhotoBlock from '../PhotoBlock';
import PhotoModal from '../PhotoModal';
import PhotoBlockSkeleton from '../PhotoBlockSkeleton';
import PhotosContainer from './PhotosContainer';
import { useDispatch } from 'react-redux';
import { challengeResults } from 'actions/challenge.actions';

const PhotoContestRank = ({
  challengeId,
  currentPhase,
}: {
  challengeId: number;
  currentPhase: ChallengePhaseType | null;
}): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [zoomedInImage, setZoomedInImage] = useState<number | null>(null);
  const [order, setOrder] = useState<ChallengeResultOrderType>('asc');

  const challengeType = CHALLENGE.PHOTO;
  const getChallengeResultsByUser = () => {
    dispatch(challengeResults.request({ challengeId, order, challengeType }));
  };

  const onCloseModal = () => {
    getChallengeResultsByUser();
    setZoomedInImage(null);
  };

  const onClickModalNext = (data: ChallengeResultsDataType) => () =>
    setZoomedInImage((prevState) => {
      const currentIndex = data.allIds.findIndex(
        (resultId) => resultId === prevState,
      );
      return data.allIds[currentIndex + 1] || data.allIds[0];
    });
  const onClickModalPrevious = (data: ChallengeResultsDataType) => () =>
    setZoomedInImage((prevState) => {
      const currentIndex = data.allIds.findIndex(
        (resultId) => resultId === prevState,
      );
      return (
        data.allIds[currentIndex - 1] || data.allIds[data.allIds.length - 1]
      );
    });
  const onImageClick = (resultId: number) => () => setZoomedInImage(resultId);

  const isDuringChallenge = currentPhase === CHALLENGE_PHASE.DURING_CHALLENGE;

  return (
    <ChallengeRanking
      title={t(
        isDuringChallenge
          ? 'photo_contest.rank.title_during'
          : 'photo_contest.rank.title_after',
      )}
      challengeId={challengeId}
      challengeType={challengeType}
      currentPhase={currentPhase}
      isVisibleWhenNoData
      ItemsContainer={PhotosContainer}
      ItemSkeleton={PhotoBlockSkeleton}
      ItemComponent={PhotoBlock}
      getItemProps={({ data, resultId }) => ({
        result: data?.byId?.[resultId],
        onImageClick: onImageClick(resultId),
        withVotingButton: isDuringChallenge,
      })}
      needValidation
      order={order}
      setOrder={setOrder}
    >
      {({ data }: ChallengeResultsStateType) => (
        <PhotoModal
          open={zoomedInImage !== null}
          result={zoomedInImage ? data?.byId?.[zoomedInImage] : undefined}
          onClose={onCloseModal}
          onClickPrevious={onClickModalPrevious(data)}
          onClickNext={onClickModalNext(data)}
          withVotingButton={isDuringChallenge}
        />
      )}
    </ChallengeRanking>
  );
};

export default PhotoContestRank;
